/**
 * Engine sim RNG — scoped injection for reproducible headless runs.
 * Prefer explicit `state.rng` / `withSimRng` over Math.random monkey-patch.
 * Phaser-free. Domain (raid bark streams) stays in feature wrappers.
 */

export type SimRng = () => number;

let scopedSimRng: SimRng | null = null;
let scopedCosmeticRng: SimRng | null = null;

/** Run `fn` with a scoped sim RNG (nested calls restore previous). */
export function withSimRng<T>(rng: SimRng, fn: () => T): T {
  const prev = scopedSimRng;
  scopedSimRng = rng;
  try {
    return fn();
  } finally {
    scopedSimRng = prev;
  }
}

/** Run `fn` with a scoped cosmetic RNG (never mixes into sim stream). */
export function withCosmeticRng<T>(rng: SimRng, fn: () => T): T {
  const prev = scopedCosmeticRng;
  scopedCosmeticRng = rng;
  try {
    return fn();
  } finally {
    scopedCosmeticRng = prev;
  }
}

/**
 * Resolve a [0,1) sim roll: explicit host.rng → scoped withSimRng → Math.random.
 * Do not use for cosmetic draws — use {@link cosmeticRandom}.
 */
export function simRandom(host?: { rng?: SimRng } | null): number {
  if (host?.rng) return host.rng();
  if (scopedSimRng) return scopedSimRng();
  return Math.random();
}

type CosmeticRngHost = {
  cosmeticRng?: SimRng;
};

/**
 * Cosmetic roll: host.cosmeticRng → scoped cosmetic → Math.random.
 * Never falls back to sim `host.rng`.
 */
export function cosmeticRandom(host?: CosmeticRngHost | null): number {
  if (host?.cosmeticRng) return host.cosmeticRng();
  if (scopedCosmeticRng) return scopedCosmeticRng();
  return Math.random();
}
